'use client';
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import classNames from 'classnames';
import { Children, isValidElement, startTransition, useCallback, useId, useMemo, useRef, useState } from 'react';
import { ButtonTab } from './button-tab';
import { scrollTabIntoView } from './helpers';
import { RadioTab } from './radio-tab';
import styles from './tabs.module.css';
const getChildrenArray = (children)=>Children.toArray(children).filter((child)=>/*#__PURE__*/ isValidElement(child));
export const Tab = ({ children })=>/*#__PURE__*/ _jsx(_Fragment, {
        children: children
    });
/**
 * `Tabs` allows simple rendering  of tabs in a content or form context.
 * When rendered in `form` mode by setting the `name` prop it will render a radio for each provided tab.
 * Also `Tab` components expect a `value` prop in `form` mode.
 *
 * @example
 * ```tsx
 *   <Tabs ariaLabel="Zahlungsmethode">
 *     <Tab label="Option with Content">
 *       SEPA Content
 *     </Tab>
 *   </Tabs>
 * ```
 *
 * @example
 * ```tsx
 *   <Tabs name="payment" ariaLabel="Zahlungsmethode" defaultValue="1">
 *     <Tab label="Option with Content" value="sepa" icon={<InvoiceIconXs />}>
 *       SEPA Content
 *     </Tab>
 *     <Tab label="Option without Content" value="manual"/>
 *   </Tabs>
 * ```
 */ export const Tabs = ({ theme = 'standard', ...props })=>{
    const id = useId();
    const childrenArray = useMemo(()=>getChildrenArray(props.children), [
        props.children
    ]);
    const hasRadioTabs = 'name' in props;
    const [active, setActive] = useState(hasRadioTabs ? childrenArray.findIndex((child)=>child.props.value === props.defaultValue) : 0);
    const buttonTabRefs = useRef([]);
    const setActiveWithTransition = useCallback((index)=>startTransition(()=>setActive(index)), []);
    const onKeyDown = useCallback((event)=>{
        const maxIndex = buttonTabRefs.current.length - 1;
        let newIndex;
        switch(event.key){
            case 'ArrowRight':
            case 'ArrowDown':
                event.preventDefault();
                newIndex = active === maxIndex ? 0 : active + 1;
                break;
            case 'ArrowLeft':
            case 'ArrowUp':
                event.preventDefault();
                newIndex = active === 0 ? maxIndex : active - 1;
                break;
            case 'Home':
                event.preventDefault();
                newIndex = 0;
                break;
            case 'End':
                event.preventDefault();
                newIndex = maxIndex;
                break;
            default:
                return;
        }
        setActiveWithTransition(newIndex);
        const buttonEl = buttonTabRefs.current[newIndex];
        if (buttonEl) {
            buttonEl.focus();
            scrollTabIntoView(buttonEl);
        }
    }, [
        active,
        setActiveWithTransition
    ]);
    return hasRadioTabs ? /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx("div", {
                className: classNames(styles['tab-bar'], styles[`theme-${theme}`]),
                children: childrenArray.map((tab, index)=>/*#__PURE__*/ _jsx(RadioTab, {
                        isActive: active === index,
                        ...tab.props,
                        id: id,
                        index: index,
                        name: props.name,
                        onChange: props.onChange,
                        setActive: setActiveWithTransition
                    }, tab.props.value))
            }),
            childrenArray.map((child, index)=>// biome-ignore lint/a11y/useSemanticElements: maybe change to tabpanel role
                /*#__PURE__*/ _jsx("div", {
                    "aria-labelledby": `${id}-radio-${index}`,
                    className: child.props?.className,
                    hidden: active !== index,
                    id: `${id}-region-${index}`,
                    role: "region",
                    children: active === index && child
                }, child.props?.value))
        ]
    }) : /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx("div", {
                "aria-label": props.ariaLabel,
                className: classNames(styles['tab-bar'], styles[`theme-${theme}`]),
                role: "tablist",
                children: childrenArray.map((tab, index)=>/*#__PURE__*/ _jsx(ButtonTab, {
                        ...tab.props,
                        active: active === index,
                        id: id,
                        index: index,
                        onKeyDown: onKeyDown,
                        ref: buttonTabRefs,
                        setActive: setActiveWithTransition
                    }, tab.props.label))
            }),
            childrenArray.map((child, index)=>/*#__PURE__*/ _jsx("div", {
                    "aria-labelledby": `${id}-tab-${index}`,
                    className: child.props?.className,
                    hidden: active !== index,
                    id: `${id}-panel-${index}`,
                    role: "tabpanel",
                    children: child
                }, child.props?.label))
        ]
    });
};

//# sourceMappingURL=index.js.map